import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Loader2, Save, Shield } from 'lucide-react';
import { toast } from 'sonner';

interface OnChainSettingsProps {
  companyId: string;
}

export default function OnChainSettings({ companyId }: OnChainSettingsProps) {
  const [enabled, setEnabled] = useState(false);
  const [chains, setChains] = useState<string[]>(['BASE']);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('onchain_settings')
        .select('enabled, chains')
        .eq('company_id', companyId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading settings:', error);
        toast.error('Failed to load settings');
      } else if (data) {
        setEnabled(data.enabled);
        setChains(data.chains || ['BASE']);
      }
    } catch (error: any) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }

  async function save() {
    setSaving(true);
    try {
      const { error } = await supabase.functions.invoke('update-onchain-settings', {
        body: { companyId, enabled, chains },
      });

      if (error) {
        console.error('Error saving settings:', error);
        toast.error('Failed to save settings');
      } else {
        toast.success('On-chain settings updated');
        load();
      }
    } catch (error: any) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  }

  function toggleChain(chain: string) {
    if (chains.includes(chain)) {
      setChains(chains.filter((c) => c !== chain));
    } else {
      setChains([...chains, chain]);
    }
  }

  useEffect(() => {
    if (companyId) {
      load();
    }
  }, [companyId]);

  if (loading) {
    return (
      <Card className="border-border bg-card">
        <CardContent className="p-6 flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border bg-card">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          <CardTitle>On-Chain Ledger Settings</CardTitle>
        </div>
        <CardDescription>
          Configure multi-chain blockchain notarization for cap table events
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="enable-notarization" className="text-sm font-medium">
              Enable Notarization
            </Label>
            <p className="text-xs text-muted-foreground">
              Automatically notarize transfers, reports, and closing packs
            </p>
          </div>
          <Switch id="enable-notarization" checked={enabled} onCheckedChange={setEnabled} />
        </div>

        {enabled && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Select Blockchains (Multi-Chain Support)
              </Label>
              <p className="text-xs text-muted-foreground">
                Choose one or more blockchains for redundant proof anchoring
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {[
                { value: 'BASE', label: 'Base (L2)' },
                { value: 'POLYGON', label: 'Polygon' },
                { value: 'ARBITRUM', label: 'Arbitrum' },
                { value: 'CHAINPOINT', label: 'Chainpoint' },
              ].map((option) => (
                <div
                  key={option.value}
                  className={`flex items-center space-x-2 p-3 border rounded-lg cursor-pointer transition-colors ${
                    chains.includes(option.value)
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => toggleChain(option.value)}
                >
                  <input
                    type="checkbox"
                    checked={chains.includes(option.value)}
                    onChange={() => toggleChain(option.value)}
                    className="h-4 w-4"
                  />
                  <Label className="cursor-pointer">{option.label}</Label>
                </div>
              ))}
            </div>

            {chains.length > 1 && (
              <p className="text-xs text-primary">
                ✓ Multi-chain redundancy enabled for {chains.length} blockchains
              </p>
            )}
          </div>
        )}

        <Button onClick={save} disabled={saving} className="w-full sm:w-auto">
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
